/**
 * RecordPurchaseModal
 *
 * Modal form for recording a new substrate log purchase.
 */

import { useState } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

export default function RecordPurchaseModal({ open, onOpenChange, onSubmit, strainId }) {
  const [formData, setFormData] = useState({
    purchase_date: toLocalDateString(new Date()),
    supplier_name: 'Glasgow Mushroom Company',
    quantity: '',
    unit_weight_kg: '2.1',
    total_cost: '',
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const quantity = parseInt(formData.quantity, 10) || 0
  const totalCostNum = parseFloat(formData.total_cost) || 0
  const costPerLog = quantity > 0 ? totalCostNum / quantity : 0

  const totalWeight = quantity && formData.unit_weight_kg
    ? (quantity * parseFloat(formData.unit_weight_kg)).toFixed(1)
    : '0.0'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      await onSubmit({
        purchase_date: formData.purchase_date,
        supplier_name: formData.supplier_name,
        strain_id: strainId,
        quantity,
        unit_weight_kg: parseFloat(formData.unit_weight_kg),
        cost_per_log: Math.round(costPerLog * 100) / 100,
        notes: formData.notes
      })
      onOpenChange(false)
      setFormData({
        purchase_date: toLocalDateString(new Date()),
        supplier_name: 'Glasgow Mushroom Company',
        quantity: '',
        unit_weight_kg: '2.1',
        total_cost: '',
        notes: ''
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Log Purchase</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="purchase_date">Purchase Date</Label>
              <Input
                id="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={(e) => handleChange('purchase_date', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier_name">Supplier</Label>
              <Input
                id="supplier_name"
                value={formData.supplier_name}
                onChange={(e) => handleChange('supplier_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity (logs)</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                placeholder="50"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_weight_kg">Weight/log (kg)</Label>
              <Input
                id="unit_weight_kg"
                type="number"
                step="0.1"
                min="0.1"
                value={formData.unit_weight_kg}
                onChange={(e) => handleChange('unit_weight_kg', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="total_cost">Total Cost (£)</Label>
              <Input
                id="total_cost"
                type="number"
                step="0.01"
                min="0"
                value={formData.total_cost}
                onChange={(e) => handleChange('total_cost', e.target.value)}
                placeholder="10080.00"
                required
              />
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total weight</span>
              <span className="font-medium">{totalWeight} kg</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cost per log</span>
              <span className="font-medium">£{costPerLog.toFixed(2)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record Purchase
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
